<template>
  <div class="view-container">
    <!-- 顶部筛选栏 -->
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-tag"></i> 日志类型</label>
          <select v-model="filterType" class="search-select">
            <option value="all">全部类型</option>
            <option value="webapi">API 操作</option>
            <option value="pageview">页面访问</option>
          </select>
        </div>
        <div class="search-item">
          <label><i class="fas fa-clock"></i> 时间范围</label>
          <select v-model="filterTimeRange" class="search-select">
            <option value="today">今天</option>
            <option value="yesterday">昨天</option>
            <option value="7d">最近7天</option>
            <option value="30d">最近30天</option>
            <option value="custom">自定义</option>
          </select>
        </div>
        <div class="search-item" v-if="filterTimeRange === 'custom'">
          <label><i class="fas fa-calendar"></i> 开始日期</label>
          <input type="date" v-model="customStart" class="search-input">
        </div>
        <div class="search-item" v-if="filterTimeRange === 'custom'">
          <label><i class="fas fa-calendar-check"></i> 结束日期</label>
          <input type="date" v-model="customEnd" class="search-input">
        </div>
        <div class="search-item">
          <label><i class="fas fa-search"></i> 关键字搜索</label>
          <input v-model="filterKeyword" type="text" class="search-input" placeholder="搜索日志内容..." @keyup.enter="searchLogs">
        </div>
        <div class="search-actions">
          <button @click="searchLogs" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
          <button @click="resetFilter" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-icon webapi">
          <i class="fas fa-globe"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.totalWebApi }}</div>
          <div class="stat-label">WebAPI总调用</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon sql">
          <i class="fas fa-database"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.totalSql }}</div>
          <div class="stat-label">SQL总执行</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon error">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.totalErrors }}</div>
          <div class="stat-label">错误次数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon slow">
          <i class="fas fa-hourglass-half"></i>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ stats.slowQueries }}</div>
          <div class="stat-label">慢查询(&gt;1000ms)</div>
        </div>
      </div>
    </div>

    <!-- 日志列表 -->
    <div class="log-table-panel">
      <div class="panel-header-bar">
        <span class="panel-title">操作日志 (共 {{ totalRecords }} 条)</span>
        <div class="panel-actions">
          <button @click="autoRefresh = !autoRefresh" class="btn btn-xs" :class="autoRefresh ? 'btn-primary' : 'btn-default'">
            <i class="fas" :class="autoRefresh ? 'fa-sync fa-spin' : 'fa-sync'"></i> {{ autoRefresh ? '自动刷新中' : '自动刷新' }}
          </button>
          <button @click="searchLogs" class="btn btn-default btn-xs">
            <i class="fas fa-sync-alt"></i> 刷新
          </button>
        </div>
      </div>
      <div class="log-table-wrapper">
        <table class="log-table">
          <thead>
            <tr>
              <th width="50">#</th>
              <th width="75">类型</th>
              <th width="65">方法</th>
              <th>路径 / 描述</th>
              <th width="80">模块</th>
              <th width="60">状态</th>
              <th width="70">耗时</th>
              <th width="65">用户</th>
              <th width="135">时间</th>
              <th width="55">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(log, idx) in pagedLogs" :key="idx" class="log-row" :class="{ 'log-error': log.status === 'error' }">
              <td class="num-cell">{{ (currentPage - 1) * pageSize + idx + 1 }}</td>
              <td>
                <span class="log-type-tag" :class="log.type === 'pageview' ? 'pageview' : 'webapi'">
                  <i :class="log.type === 'pageview' ? 'fas fa-eye' : 'fas fa-globe'"></i>
                  {{ log.type === 'pageview' ? '页面' : 'API' }}
                </span>
              </td>
              <td>
                <span class="log-method" :class="log.method">{{ log.method }}</span>
              </td>
              <td class="log-path-cell">
                <span class="log-path" :title="log.path">{{ log.path }}</span>
              </td>
              <td><span class="module-tag">{{ log.module }}</span></td>
              <td>
                <span class="log-status" :class="log.status">{{ log.status === 'success' ? '成功' : '失败' }}</span>
              </td>
              <td class="duration-cell">
                <span :class="{ 'slow': log.duration > 1000 }">{{ log.duration }}ms</span>
              </td>
              <td>{{ log.user }}</td>
              <td class="time-cell">{{ formatTime(log.time) }}</td>
              <td>
                <button class="log-detail-btn" @click="openLogDetail(log)" title="查看详情">
                  <i class="fas fa-search"></i>
                </button>
              </td>
            </tr>
            <tr v-if="pagedLogs.length === 0">
              <td colspan="10" class="empty-cell">暂无日志数据</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- 分页 -->
      <div class="log-pagination" v-if="totalPages > 1">
        <button :disabled="currentPage <= 1" @click="changePage(1)"><i class="fas fa-angle-double-left"></i></button>
        <button :disabled="currentPage <= 1" @click="changePage(currentPage - 1)"><i class="fas fa-angle-left"></i></button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)"><i class="fas fa-angle-right"></i></button>
        <button :disabled="currentPage >= totalPages" @click="changePage(totalPages)"><i class="fas fa-angle-double-right"></i></button>
      </div>
    </div>

    <!-- 日志详情弹窗 -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="showDetailModal = false">
      <div class="modal-dialog modal-wide">
        <div class="modal-header">
          <h4>
            <i :class="logDetail.type === 'pageview' ? 'fas fa-eye' : 'fas fa-globe'"></i>
            日志详情
          </h4>
          <button @click="showDetailModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">类型</span>
              <span class="log-type-tag" :class="logDetail.type === 'pageview' ? 'pageview' : 'webapi'">{{ logDetail.type === 'pageview' ? '页面访问' : 'API操作' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">方法</span>
              <span class="log-method" :class="logDetail.method">{{ logDetail.method }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">状态</span>
              <span class="log-status" :class="logDetail.status">{{ logDetail.status === 'success' ? '成功' : '失败' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">耗时</span>
              <span :class="{ 'slow': logDetail.duration > 1000 }">{{ logDetail.duration }}ms</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">模块</span>
              <span>{{ logDetail.module }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">用户</span>
              <span>{{ logDetail.user }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">IP地址</span>
              <span>{{ logDetail.ip }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">时间</span>
              <span>{{ formatTime(logDetail.time) }}</span>
            </div>
            <div class="detail-item full-width">
              <span class="detail-label">{{ logDetail.type === 'pageview' ? '页面路径' : '请求路径' }}</span>
              <code class="detail-code">{{ logDetail.path }}</code>
            </div>
            <div class="detail-item full-width" v-if="logDetail.requestBody">
              <span class="detail-label">请求体</span>
              <pre class="detail-pre">{{ logDetail.requestBody }}</pre>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showDetailModal = false" class="btn btn-default">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { getAuditLogs } from '../api/audit-log'

const filterType = ref('all')
const filterTimeRange = ref('today')
const filterKeyword = ref('')
const customStart = ref('')
const customEnd = ref('')
const currentPage = ref(1)
const pageSize = ref(20)
const autoRefresh = ref(false)
const showDetailModal = ref(false)
const logDetail = ref({})
const allLogs = ref([])
const totalRecords = ref(0)
const loading = ref(false)

let refreshTimer = null

// 时间范围映射
const getTimeRange = () => {
  const now = new Date()
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  let start
  switch (filterTimeRange.value) {
    case 'today': start = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break
    case 'yesterday': start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1); break
    case '7d': start = new Date(now.getTime() - 7 * 86400000); break
    case '30d': start = new Date(now.getTime() - 30 * 86400000); break
    case 'custom': {
      start = customStart.value ? new Date(customStart.value) : new Date(now.getTime() - 7 * 86400000)
      return { startTime: start.toISOString(), endTime: (customEnd.value ? new Date(customEnd.value + 'T23:59:59') : end).toISOString() }
    }
    default: start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }
  return { startTime: start.toISOString(), endTime: end.toISOString() }
}

async function searchLogs() {
  currentPage.value = 1
  await loadLogs()
}

async function loadLogs() {
  loading.value = true
  try {
    const { startTime, endTime } = getTimeRange()
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value,
      keyword: filterKeyword.value || undefined,
      startTime,
      endTime
    }
    // 动作类型过滤
    if (filterType.value === 'webapi') {
      params.actionType = 'POST,PUT,DELETE,PATCH'
    } else if (filterType.value === 'sql') {
      // SQL 操作通过 Module 或 Action 中的关键字区分，暂不过滤
    } else if (filterType.value === 'pageview') {
      params.actionType = 'PAGE_VIEW'
    }
    const res = await getAuditLogs(params)
    totalRecords.value = (res?.total || res?.Total || 0)
    const rawData = res?.data || res?.Data || []
    allLogs.value = rawData.map(log => ({
      guid: log.guid || log.Guid || '',
      type: log.actionType === 'PAGE_VIEW' ? 'pageview' : 'webapi',
      method: log.requestMethod || log.RequestMethod || log.actionType || '',
      path: log.requestUrl || log.RequestUrl || log.action || log.Action || '',
      targetName: log.targetName || log.TargetName || '',
      status: (log.responseStatus || log.ResponseStatus || 200) >= 400 ? 'error' : 'success',
      duration: log.duration || log.Duration || 0,
      user: log.operatorName || log.OperatorName || log.operatorAccount || log.OperatorAccount || '',
      ip: log.operatorIP || log.OperatorIP || '',
      time: log.createTime || log.CreateTime || '',
      requestBody: log.requestBody || log.RequestBody || '',
      module: log.module || log.Module || '',
      actionType: log.actionType || log.ActionType || ''
    }))
  } catch (e) {
    console.error('加载日志失败:', e)
    allLogs.value = []
    totalRecords.value = 0
  } finally {
    loading.value = false
  }
}

const filteredLogs = computed(() => {
  let logs = allLogs.value

  if (filterType.value !== 'all' && filterType.value !== 'webapi' && filterType.value !== 'pageview') {
    logs = logs.filter(l => l.type === filterType.value)
  }

  return logs
})

const totalPages = computed(() => Math.max(1, Math.ceil(totalRecords.value / pageSize.value)))

const pagedLogs = computed(() => {
  return filteredLogs.value
})

const stats = computed(() => {
  const logs = allLogs.value
  return {
    totalWebApi: totalRecords.value,
    totalSql: logs.filter(l => l.type === 'sql').length,
    totalErrors: logs.filter(l => l.status === 'error').length,
    slowQueries: logs.filter(l => l.duration > 1000).length
  }
})

function resetFilter() {
  filterType.value = 'all'
  filterTimeRange.value = 'today'
  filterKeyword.value = ''
  customStart.value = ''
  customEnd.value = ''
  currentPage.value = 1
  loadLogs()
}

function openLogDetail(log) {
  logDetail.value = { ...log }
  showDetailModal.value = true
}

function changePage(p) {
  currentPage.value = p
  loadLogs()
}

function formatTime(time) {
  if (!time) return ''
  // 后端返回的是 ISO 8601 格式: 2025-07-19T10:30:00
  return time.replace('T', ' ').substring(0, 19)
}

onMounted(() => {
  loadLogs()
  if (autoRefresh.value) {
    refreshTimer = setInterval(loadLogs, 10000)
  }
})

watch(autoRefresh, (val) => {
  if (val) {
    refreshTimer = setInterval(loadLogs, 10000)
  } else {
    if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null }
  }
})

onUnmounted(() => {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null }
})
</script>

<style scoped>
.view-container {
  padding: 12px;
  background: #f5f7fa;
  min-height: calc(94vh - 110px);
  max-height: calc(94vh - 110px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 搜索面板 */
.search-panel {
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.search-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.search-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.search-item label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

.search-item label i {
  color: #10b981;
  font-size: 11px;
}

.search-input, .search-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  background: #fff;
  min-width: 140px;
  height: 32px;
}

.search-input:focus, .search-select:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16,185,129,.1);
}

.search-input { min-width: 180px; }

.search-select { min-width: 120px; }

.search-actions {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding-bottom: 1px;
}

.search-btn {
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 32px;
}

.btn { padding: 5px 12px; border-radius: 4px; font-size: 11px; cursor: pointer; }
.btn-primary { background: #10b981; border: none; color: #fff; }
.btn-primary:hover { background: #059669; }
.btn-default { background: #fff; border: 1px solid #d1d5db; color: #374151; }
.btn-default:hover { background: #f9fafb; }

/* 统计卡片 */
.stats-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.stat-card {
  flex: 1;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.stat-icon.webapi { background: #eff6ff; color: #3b82f6; }
.stat-icon.sql { background: #f0fdf4; color: #10b981; }
.stat-icon.error { background: #fef2f2; color: #ef4444; }
.stat-icon.slow { background: #fffbeb; color: #f59e0b; }

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.stat-label { font-size: 11px; color: #6b7280; margin-top: 2px; }

/* 日志列表面板 */
.log-table-panel {
  flex: 1;
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.panel-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #e8ecf1;
  flex-shrink: 0;
}

.panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.panel-actions { display: flex; gap: 6px; }

.log-table-wrapper {
  flex: 1;
  overflow: auto;
}

.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.log-table thead th {
  padding: 8px 10px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  background: #f8fafc;
  border-bottom: 1px solid #e8ecf1;
  position: sticky;
  top: 0;
  z-index: 2;
}

.log-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.log-row { transition: background 0.1s; }

.log-row:hover { background: #f9fafb; }

.log-row.log-error { background: #fef2f2; }
.log-row.log-error:hover { background: #fee2e2; }

.num-cell { color: #9ca3af; font-size: 11px; }

.log-type-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
}

.log-type-tag.webapi { background: #eff6ff; color: #3b82f6; }
.log-type-tag.sql { background: #f0fdf4; color: #059669; }
.log-type-tag.pageview { background: #f5f3ff; color: #7c3aed; }

.log-method {
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  font-family: monospace;
}

.log-method.GET { background: #dbeafe; color: #1d4ed8; }
.log-method.POST { background: #d1fae5; color: #047857; }
.log-method.PUT { background: #fef3c7; color: #b45309; }
.log-method.DELETE { background: #fee2e2; color: #dc2626; }
.log-method.SELECT { background: #dbeafe; color: #1d4ed8; }
.log-method.INSERT { background: #d1fae5; color: #047857; }
.log-method.UPDATE { background: #fef3c7; color: #b45309; }
.log-method.EXEC { background: #ede9fe; color: #6d28d9; }
.log-method.PAGE_VIEW { background: #f5f3ff; color: #7c3aed; }

.log-path-cell { max-width: 400px; }

.log-path {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 11px;
}

.log-status {
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.log-status.success { background: #d1fae5; color: #059669; }
.log-status.error { background: #fee2e2; color: #ef4444; }
.log-status.warning { background: #fef3c7; color: #b45309; }

.module-tag { background: #e0e7ff; color: #4338ca; padding: 1px 8px; border-radius: 8px; font-size: 10px; }

.duration-cell { font-family: monospace; font-size: 11px; }

.duration-cell .slow { color: #ef4444; font-weight: 600; }

.time-cell { font-size: 11px; color: #9ca3af; white-space: nowrap; }

.log-detail-btn {
  width: 24px; height: 24px; border: 1px solid #e5e7eb;
  border-radius: 3px; background: #fff; color: #6366f1;
  cursor: pointer; font-size: 10px;
  display: flex; align-items: center; justify-content: center;
}

.log-detail-btn:hover { background: #6366f1; color: #fff; border-color: #6366f1; }

.empty-cell { text-align: center; padding: 40px; color: #9ca3af; }

/* 分页 */
.log-pagination {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px; border-top: 1px solid #e8ecf1; flex-shrink: 0;
}

.log-pagination button {
  width: 28px; height: 28px; border: 1px solid #d1d5db;
  border-radius: 4px; background: #fff; cursor: pointer;
  font-size: 11px; color: #374151;
  display: flex; align-items: center; justify-content: center;
}

.log-pagination button:hover:not(:disabled) { background: #f3f4f6; }
.log-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }

.page-info { font-size: 12px; color: #6b7280; min-width: 60px; text-align: center; }

/* 弹窗 */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}

.modal-dialog {
  background: #fff; border-radius: 6px; width: 700px;
  max-width: 90vw; max-height: 85vh; display: flex; flex-direction: column;
}

.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 14px; border-bottom: 1px solid #e8ecf1; flex-shrink: 0;
}

.modal-header h4 { margin: 0; font-size: 14px; color: #1f2937; display: flex; align-items: center; gap: 8px; }

.modal-header h4 i { color: #10b981; }

.modal-close {
  width: 24px; height: 24px; border: none; background: none;
  font-size: 18px; color: #9ca3af; cursor: pointer;
}

.modal-body { padding: 16px; overflow-y: auto; flex: 1; }

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.detail-item { }

.detail-item.full-width { grid-column: 1 / -1; }

.detail-label {
  display: block; font-size: 10px; font-weight: 600;
  color: #6b7280; margin-bottom: 4px; text-transform: uppercase;
}

.detail-code, .detail-pre {
  display: block; padding: 10px; background: #1e293b; color: #e2e8f0;
  border-radius: 4px; font-size: 12px; font-family: 'Consolas', 'Courier New', monospace;
  line-height: 1.6; overflow-x: auto; white-space: pre-wrap; word-break: break-all;
}

.error-pre { background: #fef2f2; color: #ef4444; }

.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 10px 14px; border-top: 1px solid #e8ecf1; flex-shrink: 0;
}

.btn { padding: 5px 12px; border-radius: 4px; font-size: 11px; cursor: pointer; }
.btn-default { background: #fff; border: 1px solid #d1d5db; color: #374151; }
.btn-default:hover { background: #f9fafb; }
.btn-primary { background: #10b981; border: none; color: #fff; }
.btn-primary:hover { background: #059669; }
.btn-xs { padding: 3px 8px; font-size: 10px; }
.btn-sm { padding: 5px 12px; font-size: 12px; }
</style>
